// Gets a layer's glyph into the map's sprite so a symbol layer can draw it.
//
// A glyph is a Material Symbols ligature: the CMS stores a symbol's name and
// the font swaps the name for its drawing. An image added through addImage is
// a raster Mapbox's icon-color cannot tint — only SDF icons take it — so the
// sprite holds one image per icon-and-colour pair, drawn onto a canvas in the
// layer's colour.

// Matches how the picker's swatch draws the same name; the face itself arrives
// with the stylesheet in HybridActivityMap.module.scss, which pins the fill
// axis — at marker size the outlined default is too fine to read.
const GLYPH_FONT = '400 24px "Material Symbols Rounded Filled"';

// The sprite is shared with the basemap's own images, hence the prefix. The
// colour is part of the id because it is baked into the raster.
export const iconImageId = (icon, colour) =>
    `hybrid-activity-${icon}-${colour}`;

// Drawn at twice the size it displays at, so the glyph stays sharp on retina.
// Displays at 12px on the 24px disc, matching the design's ink-to-disc ratio.
const ICON_PIXEL_RATIO = 2;
const ICON_SIZE = 24;

const rasterise = (icon, colour) => {
    const canvas = document.createElement('canvas');
    canvas.width = ICON_SIZE;
    canvas.height = ICON_SIZE;

    const context = canvas.getContext('2d');
    context.font = GLYPH_FONT;
    context.fillStyle = colour;

    // Centred on the glyph's ink, not its em box: Material Symbols ink sits
    // above the baseline and off-centre of the advance, so textAlign/-Baseline
    // centring put the glyph up and left of its disc.
    const ink = context.measureText(icon);
    context.fillText(
        icon,
        (ICON_SIZE + ink.actualBoundingBoxLeft - ink.actualBoundingBoxRight) /
            2,
        (ICON_SIZE +
            ink.actualBoundingBoxAscent -
            ink.actualBoundingBoxDescent) /
            2
    );

    return context.getImageData(0, 0, ICON_SIZE, ICON_SIZE);
};

const addIcon = async (map, { icon, colour }) => {
    try {
        // Awaited before the canvas draws: a ligature drawn before its font is
        // in comes out as the name spelled out in the fallback font. Resolving
        // with no faces is how a browser says the family is unknown.
        const faces = await document.fonts.load(GLYPH_FONT, icon);

        if (!faces.length) {
            throw new Error('font unavailable');
        }

        const id = iconImageId(icon, colour);

        // The style can reload between the request and the response.
        if (!map.hasImage(id)) {
            map.addImage(id, rasterise(icon, colour), {
                pixelRatio: ICON_PIXEL_RATIO,
            });
        }

        return id;
    } catch {
        console.warn(`Hybrid activity: no glyph drawn for "${icon}".`);
        return null;
    }
};

/**
 * Load every icon-and-colour pair the configured layers ask for.
 *
 * Resolves with the image ids that made it, so a glyph that cannot be drawn
 * costs that one layer its glyph — its bordered discs still draw — rather than
 * the whole symbol layer. Never rejects, for the same reason.
 */
export const loadLayerIcons = async (map, layers) => {
    const wanted = new Map();

    layers.forEach(({ icon, colour }) => {
        if (icon) {
            wanted.set(iconImageId(icon, colour), { icon, colour });
        }
    });

    const loaded = await Promise.all(
        [...wanted.entries()].map(([id, pair]) =>
            map.hasImage(id) ? id : addIcon(map, pair)
        )
    );

    return loaded.filter(Boolean);
};
